"use client";

import { formatCurrency } from "@/lib/format";


import type { MarketDevice } from "@/features/devices/types";
import {
  LANDING_PAGE_SIZE,
  LANDING_TITLE,
} from "@/features/marketing/components/landing/landing-content";
import {
  CountryFlagChip,
  deviceSpecs,
  deviceTitle,
  LandingResults,
  LandingSearch,
  offeredPrices,
  OwnerByline,
  RentRow,
  useLandingMarket,
} from "@/features/marketing/components/landing/landing-shared";

/**
 * The landing page (`/`) — "Centered hero + stat strip", the approved design
 * (variant D of the 6f7e289 bake-off, locked in 2026-07-23).
 *
 * A centered hero with the search as its single focal object, one hairline stat
 * strip that turns the live fleet into the page's proof, then the device grid.
 *
 * Three sections are deliberately ABSENT and must stay that way: a feature
 * grid, a "why us" band and a closing CTA. Signed out, live xgodo.com is one
 * headline followed immediately by the searchable marketplace — the devices are
 * the pitch. The pre-redesign v5 landing had invented all three, which is the
 * drift this redesign exists to undo. See `landing-content` for the full parity
 * contract.
 */
function StatStrip({
  devices,
  total,
}: {
  devices: MarketDevice[];
  total: number;
}) {
  const online = devices.filter((d) => d.online).length;
  const countries = new Set(devices.map((d) => d.country).filter(Boolean)).size;
  const cheapest = devices
    .flatMap((d) => offeredPrices(d).map((p) => p.amount))
    .sort((a, b) => a - b)[0];

  const stats = [
    { label: "Devices available", value: String(total) },
    { label: "Online now", value: String(online) },
    { label: "Countries", value: String(countries) },
    { label: "From", value: cheapest == null ? "—" : formatCurrency(cheapest) },
  ];

  return (
    <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-hairline bg-hairline lg:grid-cols-4">
      {stats.map(({ label, value }) => (
        <div key={label} className="bg-elevated px-6 py-5 text-center">
          <dt className="font-mono text-[0.65rem] uppercase tracking-[0.16em] text-muted-foreground">
            {label}
          </dt>
          <dd className="mt-1.5 font-mono text-2xl font-semibold tabular-nums tracking-tight">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function DeviceCard({ device }: { device: MarketDevice }) {
  return (
    <article className="flex flex-col rounded-xl border border-hairline bg-card transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:bg-accent">
      <div className="flex items-start justify-between gap-3 border-b border-hairline p-5">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold tracking-tight">
            {deviceTitle(device)}
          </h3>
          <OwnerByline device={device} className="mt-1 text-xs" />
        </div>
        <CountryFlagChip code={device.country} />
      </div>

      <dl className="flex-1 space-y-2.5 p-5">
        {deviceSpecs(device).map(({ label, value }) => (
          <div
            key={label}
            className="flex items-baseline justify-between gap-4"
          >
            <dt className="text-xs uppercase tracking-[0.1em] text-muted-foreground">
              {label}
            </dt>
            <dd className="truncate text-sm font-medium">{value}</dd>
          </div>
        ))}
      </dl>

      <RentRow device={device} className="border-t border-hairline p-4" />
    </article>
  );
}

export function LandingView() {
  const { input, setInput, search, submit, query } = useLandingMarket();

  return (
    <div className="pb-20">
      <section className="relative overflow-hidden border-b border-hairline">
        <div className="container relative flex flex-col items-center gap-7 py-16 text-center md:py-24">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Rent · Automate · Earn
          </p>
          <h1 className="max-w-4xl text-4xl font-semibold tracking-tight md:text-6xl">
            {LANDING_TITLE}
          </h1>
          <LandingSearch
            input={input}
            setInput={setInput}
            onSubmit={submit}
            pending={query.isFetching}
            className="max-w-xl"
            inputClassName="h-12"
          />
        </div>
      </section>

      <div className="container space-y-10 pt-10">
        <LandingResults
          query={query}
          search={search}
          gridClassName="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-hairline bg-hairline lg:grid-cols-4"
          skeletonClassName="h-[5.75rem] w-full rounded-none"
          skeletonCount={4}
          quiet
        >
          {(devices, total) => <StatStrip devices={devices} total={total} />}
        </LandingResults>

        <LandingResults query={query} search={search}>
          {(devices) => (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {devices.slice(0, LANDING_PAGE_SIZE).map((device) => (
                <DeviceCard key={device.deviceId} device={device} />
              ))}
            </div>
          )}
        </LandingResults>
      </div>
    </div>
  );
}
